/**
 * React Context for Trimble Connect Workspace API
 * Simplified version
 */

import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  initWorkspaceAPI,
  getAPI,
  getHostInfo,
} from "../services/workspaceService";
import type { HostInfo } from "../types/workspace";
import { getTokenScopes } from "./tokenScopes";

const REQUIRED_SCOPES = (import.meta.env.VITE_TID_SCOPES as string)
  .split(",")
  .map((s: string) => s.trim());

interface WorkspaceContextValue {
  isReady: boolean;
  isLoading: boolean;
  /** True when the host token lacks required scopes (older Quadri versions). */
  isRetired: boolean;
  /** Lower-cased scopes on the host token; empty until the token is accepted. */
  tokenScopes: string[];
  error: string | null;
  accessToken: string;
  api: any;
  hostInfo: HostInfo | null;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

interface WorkspaceProviderProps {
  children: ReactNode;
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetired, setIsRetired] = useState(false);
  const [tokenScopes, setTokenScopes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState("");
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const token = await initWorkspaceAPI();

        const host = await getHostInfo();
        setHostInfo(host);

        // Check if the token contains all required scopes
        const tokenScopes = getTokenScopes(token);
        const lowerTokenScopes = tokenScopes.map((s) => s.toLowerCase());
        const missingScopes = REQUIRED_SCOPES.filter(
          (s) => !lowerTokenScopes.includes(s.toLowerCase()),
        );

        if (missingScopes.length > 0) {
          console.warn(
            "[WorkspaceProvider] Token missing required scopes:",
            missingScopes,
          );
          setIsRetired(true);
          return;
        }

        setAccessToken(token);
        setTokenScopes(lowerTokenScopes);
        setIsReady(true);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to connect";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const value: WorkspaceContextValue = {
    isReady,
    isLoading,
    isRetired,
    tokenScopes,
    error,
    accessToken,
    api: getAPI(),
    hostInfo,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

/**
 * Hook to access the Workspace API context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}
